import { computed, ref, watch } from 'vue'
import api from '@/services/api'
import { useTonAddress, useTonConnectUI } from '@townsquarelabs/ui-vue'
import { useAnalytics } from '@/composables/analytics'
import { beginCell, Cell } from '@ton/ton'
import { nanoid } from 'nanoid'
import { env } from '@/env'
import { player } from '@/composables/game/player'

export class TxFailed extends Error {
}

export const useWalletConnect = () => {
  const rawAddress = useTonAddress(false)
  const userFriendlyAddress = useTonAddress(true)
  const { triggerEvent } = useAnalytics()
  const [tonConnectUI] = useTonConnectUI()
  watch(rawAddress, async rawAddress => {
    if (!rawAddress) return
    if (rawAddress !== player.value.settings.tonWalletAddress) {
      await api.setWalletAddress('ton', rawAddress)
      triggerEvent({ event: 'connect_ton_wallet' })
      player.value.settings.tonWalletAddress = rawAddress
    }
  }, { immediate: true })

  async function doTonPurchaseTransaction({ tonAmount, nonce }: { nonce: string, tonAmount: number }) {
    try {
      await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 90,
        messages: [
          {
            address: env.shop.contractAddress,
            amount: String(Math.round(tonAmount * 1000000000)),
            payload: beginCell()
              .storeUint(330163734, 32)
              .storeStringRefTail(nonce)
              .endCell()
              .toBoc()
              .toString('base64'),
          },
        ],
      })
    } catch (error) {
      const errorMessage = (error as Error).message
      await api.reportError({
        code: 'ton_payment_failed',
        metadata: JSON.parse(JSON.stringify(error)),
        description: errorMessage,
      })
      if (errorMessage.includes('UserRejectsError') || errorMessage.includes('Transaction was not sent')) {
        throw new TxFailed()
      }
      throw new Error()
    }
  }

  async function doLingoRaffleTx(contractAddress: string, amount = '10000000', isOffer?: boolean): Promise<string> {
    try {
      const { boc } = await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 90, // 60 sec
        messages: [
          {
            address: contractAddress,
            amount: amount,
            payload: beginCell().storeUint(3841462256, 32).endCell().toBoc().toString('base64'),
          },
        ],
      })
      const cell = Cell.fromBase64(boc)
      const buffer = cell.hash()
      return buffer.toString('hex')
    } catch (error) {
      const errorMessage = (error as Error).message
      api.reportError({
        code: 'tx_failed',
        metadata: {},
        description: errorMessage,
      }).then(() => {
      })
      if (errorMessage.includes('UserRejectsError') || errorMessage.includes('Transaction was not sent')) {
        throw new TxFailed(errorMessage)
      }
      if (errorMessage.includes('Context must be an Activity') && isOffer) {
        throw new TxFailed(errorMessage)
      }
      return nanoid(20)
    }
  }

  return {
    userFriendlyAddress,
    rawAddress,
    doLingoRaffleTx: doLingoRaffleTx,
    doTonPurchaseTransaction: doTonPurchaseTransaction,
    isTonWalletConnected: computed(() => Boolean(rawAddress.value)),
  }
}

export const useDisconnectWallet = () => {
  const [tonConnectUI] = useTonConnectUI()
  const isDisconnecting = ref(false)

  async function disconnectWallet() {
    isDisconnecting.value = true
    try {
      await tonConnectUI.disconnect()
    } finally {
      isDisconnecting.value = false
    }
  }

  return {
    disconnectWallet,
    isDisconnecting,
  }
}
